import { apiClient, clearCsrfToken } from '@/utils/api-client';

interface SessionTokenResponse {
  admin: {
    id: string;
    type: 'prosci_admin' | 'license_admin';
    level?: string; // 'super', 'admin', 'affiliate_admin', 'affiliate', 'data'
    affiliate_id?: string;
    email: string;
    name: string;
    permissions: string[];
    organization_id?: string;
    customer_type_id?: number;
  };
}

class SessionAuthService extends EventTarget {
  private ADMIN_INFO_KEY = 'prosci_admin_info';

  constructor() {
    super();
  }

  /**
   * Authenticate a Prosci admin using Auth0
   */
  async authenticateProsciAdmin(auth0Sub: string, email?: string, auth0Sid?: string, idToken?: string): Promise<SessionTokenResponse> {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',  // Important: include cookies
      body: JSON.stringify({
        auth_type: 'auth0_admin',
        id_token: idToken,
        auth0_sub: auth0Sub,
        email: email,
        auth0_sid: auth0Sid,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Authentication failed');
    }

    const data: SessionTokenResponse = await response.json();
    clearCsrfToken();
    this.storeSession(data);
    return data;
  }

  /**
   * Authenticate a License Admin using content_hub AES encrypted token
   */
  async authenticateLicenseAdmin(contentHubToken: string): Promise<SessionTokenResponse> {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',  // Important: include cookies
      body: JSON.stringify({
        auth_type: 'content_hub',
        token: contentHubToken,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Authentication failed');
    }

    const data: SessionTokenResponse = await response.json();
    clearCsrfToken();
    this.storeSession(data);
    return data;
  }

  /**
   * Store session data
   */
  private storeSession(data: SessionTokenResponse): void {
    // Store admin info in sessionStorage for UI purposes only
    // The actual session token is in an httpOnly cookie managed by the API
    sessionStorage.setItem(this.ADMIN_INFO_KEY, JSON.stringify(data.admin));

    // Store admin ID for backward compatibility
    localStorage.setItem('current_user_id', data.admin.id);

    // Dispatch event to notify components of session change
    this.dispatchEvent(new CustomEvent('sessionChanged', { detail: { authenticated: true } }));
  }

  /**
   * Get current session token - now always returns null since we use cookies
   * Kept for backward compatibility
   */
  getSessionToken(): string | null {
    // Tokens are now in httpOnly cookies, not accessible from JavaScript
    return null;
  }

  /**
   * Get current admin info
   */
  getAdminInfo(): SessionTokenResponse['admin'] | null {
    const adminInfo = sessionStorage.getItem(this.ADMIN_INFO_KEY);
    return adminInfo ? JSON.parse(adminInfo) : null;
  }

  /**
   * Set admin info directly (used when restoring admin session after masquerading)
   */
  setAdminInfo(adminInfo: SessionTokenResponse['admin']): void {
    sessionStorage.setItem(this.ADMIN_INFO_KEY, JSON.stringify(adminInfo));
    localStorage.setItem('current_user_id', adminInfo.id);
    this.dispatchEvent(new CustomEvent('sessionChanged', { detail: { authenticated: true } }));
  }

  /**
   * Check if user is authenticated
   * Since tokens are in httpOnly cookies, we check if admin info exists
   */
  isAuthenticated(): boolean {
    const adminInfo = this.getAdminInfo();
    return adminInfo !== null;
  }

  /**
   * Clear session
   * @param options.skipEvent - Skip dispatching sessionChanged event (use when redirecting away)
   */
  clearSession(options?: { skipEvent?: boolean }): void {
    // Clear stored admin info
    sessionStorage.removeItem(this.ADMIN_INFO_KEY);
    localStorage.removeItem('current_user_id');

    // Clear CSRF token
    clearCsrfToken();

    // Call API to clear the httpOnly cookie
    fetch(`${import.meta.env.VITE_API_BASE_URL}/api/auth/token`, {
      method: 'DELETE',
      credentials: 'include',
    }).catch(() => {
      // Ignore errors - cookie might already be cleared
    });

    // Dispatch event to notify components of session change
    // Skip if we're about to redirect anyway (prevents race condition with ProtectedRoute)
    if (!options?.skipEvent) {
      this.dispatchEvent(new CustomEvent('sessionChanged', { detail: { authenticated: false } }));
    }
  }

  /**
   * Initialize service (check for existing session)
   */
  initialize(): void {
    // No initialization needed - cookies are automatically sent
    // Just check if we have admin info
    const adminInfo = this.getAdminInfo();
    if (adminInfo) {
      // Session exists, dispatch event
      this.dispatchEvent(new CustomEvent('sessionChanged', { detail: { authenticated: true } }));
    }
  }
}

export const sessionAuthService = new SessionAuthService();